/* The home section keeps each product card intact while moving the existing row. */
if (!customElements.get('oe-home-product-slider')) {
  let sliderOrder = 0;

  customElements.define('oe-home-product-slider', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.grid = this.querySelector('.oe-home-products__grid');
      this.viewport = this.querySelector('.oe-home-products__viewport');
      if (!this.grid) return;
      if (!this.closest('.oe-home-products--carousel')) {
        for (const type of ['touchstart', 'touchmove', 'touchend', 'touchcancel', 'pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
          this.viewport.addEventListener(type, (event) => {
            if (matchMedia('(max-width: 767px)').matches && event.target.closest('oe-card-gallery')) event.stopPropagation();
          }, { signal, capture: true, passive: true });
        }
        return;
      }

      this.order = sliderOrder++;
      this.inView = false;
      this.hovered = false;
      this.focused = false;
      this.touching = false;
      this.animating = false;
      this.position = 0;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-home-product-step]');
        if (!button || button.disabled) return;
        event.preventDefault();
        this.stopTimer();
        this.step(Number(button.dataset.homeProductStep));
        this.schedule();
      }, { signal });

      this.addEventListener('mouseenter', () => { this.hovered = true; this.stopTimer(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.stopTimer(); }, { signal });
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.focused = this.contains(document.activeElement);
          this.schedule();
        });
      }, { signal });

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        this.touching = true;
        this.touchStart = { x: event.clientX, y: event.clientY };
        this.stopTimer();
      }, { signal, passive: true, capture: true });
      this.viewport.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'touch' || !this.touchStart) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        this.touching = false;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.swiped = true;
          this.step(dx < 0 ? 1 : -1);
          window.setTimeout(() => { this.swiped = false; }, 500);
        }
        this.schedule();
      }, { signal, passive: true, capture: true });
      this.viewport.addEventListener('pointercancel', () => {
        this.touchStart = null;
        this.touching = false;
        this.schedule();
      }, { signal });
      this.viewport.addEventListener('click', (event) => {
        if (this.swiped) { event.preventDefault(); event.stopPropagation(); }
      }, { signal, capture: true });
      // On mobile, a swipe on the card image navigates products, not the card's image gallery.
      for (const type of ['touchstart', 'touchmove', 'touchend', 'touchcancel', 'pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
        this.viewport.addEventListener(type, (event) => {
          if (matchMedia('(max-width: 767px)').matches && event.target.closest('oe-card-gallery')) event.stopPropagation();
        }, { signal, capture: true, passive: true });
      }

      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.grid.classList.toggle('is-ready', this.inView);
        if (this.inView) this.loadNextImage();
        this.schedule();
      }, { threshold: 0.1 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.stopTimer();
      this.finishAnimation?.();
      this.observer?.disconnect();
      this.controller?.abort();
    }

    stopTimer() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    schedule() {
      this.stopTimer();
      if (!this.isConnected || !this.inView || document.hidden || this.hovered || this.focused || this.touching) return;
      const delay = this.firstRun ? 6000 : 6000 + this.order * 650;
      this.firstRun = true;
      this.timer = window.setTimeout(() => {
        this.step(1);
        this.schedule();
      }, delay);
    }

    loadNextImage() {
      const visible = Number(getComputedStyle(this.grid).getPropertyValue('--oe-home-columns')) || 4;
      this.grid.children[visible]?.querySelector('img')?.setAttribute('loading', 'eager');
    }

    step(direction) {
      if (this.animating || this.grid.children.length < 2 || (direction < 0 && this.position === 0)) return;
      this.animating = true;
      const first = this.grid.firstElementChild;
      const distance = first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.grid).columnGap);
      this.direction = direction;
      if (direction < 0) {
        this.grid.prepend(this.grid.lastElementChild);
        this.grid.style.transform = `translate3d(-${distance}px, 0, 0)`;
      }
      const from = direction > 0 ? 'translate3d(0, 0, 0)' : `translate3d(-${distance}px, 0, 0)`;
      const to = direction > 0 ? `translate3d(-${distance}px, 0, 0)` : 'translate3d(0, 0, 0)';
      this.animation = this.grid.animate([{ transform: from }, { transform: to }], {
        duration: 1200,
        easing: 'cubic-bezier(.32, .2, .68, .8)',
        fill: 'forwards',
      });
      this.animation.finished.then(() => this.finishAnimation()).catch(() => {});
    }

    finishAnimation() {
      if (!this.animating) return;
      if (this.direction > 0) this.grid.append(this.grid.firstElementChild);
      this.grid.style.transform = 'translate3d(0, 0, 0)';
      this.animation?.cancel();
      this.animation = null;
      this.position = (this.position + this.direction + this.grid.children.length) % this.grid.children.length;
      this.querySelector('[data-home-product-step="-1"]').disabled = this.position === 0;
      this.animating = false;
      this.loadNextImage();
    }
  });
}
